import React from 'react';
import { Alert, AlertTitle, Button, Link as MuiLink, Box, Typography } from '@mui/material';
import { Info as InfoIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuota } from '../../contexts/QuotaContext'; // Adjust path as necessary

const QuotaUsageBanner: React.FC = () => {
  const {
    showQuotaBanner,
    dismissBanner,
    simulationsLeft,
    daysUntilQuotaReset,
    subscriptionInfo
  } = useQuota();

  if (!showQuotaBanner) {
    return null;
  }

  const quotaPercentage = subscriptionInfo?.monthlySimQuota && subscriptionInfo?.simUsed
    ? Math.round((subscriptionInfo.simUsed / subscriptionInfo.monthlySimQuota) * 100)
    : 0;

  return (
    <Alert
      severity="warning"
      icon={<InfoIcon fontSize="inherit" />}
      action={
        <Button color="inherit" size="small" onClick={dismissBanner} aria-label="Dismiss quota warning">
          <CloseIcon fontSize="small" />
        </Button>
      }
      sx={{
        mb: 2,
        borderRadius: '4px',
        border: '1px solid', 
        borderColor: 'warning.light', 
        backgroundColor: 'warning.lighter', // A very light yellow/orange
        color: 'warning.darker',
        alignItems: 'center',
        '.MuiAlert-message': {
          width: '100%', // Ensure message area takes full width
        }
      }}
    >
      <AlertTitle sx={{ fontWeight: 'bold' }}>Quota Usage Warning</AlertTitle>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Typography variant="body2" sx={{ flexGrow: 1, mb: { xs: 1, sm: 0 }, mr: {sm: 2} }}>
          Your organization has used <strong>{quotaPercentage}%</strong> of its monthly simulation quota.
          You have <strong>{simulationsLeft ?? 'N/A'} simulation{simulationsLeft === 1 ? '' : 's'} left</strong>.
          {daysUntilQuotaReset !== null && (
            <span> Your quota resets in <strong>{daysUntilQuotaReset} day{daysUntilQuotaReset === 1 ? '' : 's'}</strong>.</span>
          )}
        </Typography>
        <MuiLink
          component={RouterLink}
          to="/settings?section=billing"
          variant="body2"
          sx={{
            fontWeight: 'medium',
            color: 'warning.dark',
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'none',
            },
            whiteSpace: 'nowrap' // Prevent wrapping
          }}
        >
          Manage Subscription
        </MuiLink>
      </Box>
    </Alert>
  );
};

export default QuotaUsageBanner; 